/**
 * Helpers for reading what a bulk endpoint sent back.
 *
 * The backend reports the outcome of each row inside a downloadable result
 * file, so the job here is to recognise that file, name it, and hand it over -
 * plus pull a readable message out of a JSON or plain-text body.
 */

export const RESPONSE_KIND = {
  FILE: "FILE",
  JSON: "JSON",
  TEXT: "TEXT",
};

const FILE_CONTENT_HINTS = [
  "spreadsheet",
  "excel",
  "officedocument",
  "csv",
  "octet-stream",
];

const FILE_NAME_HINTS = [".xlsx", ".xls", ".csv"];

const MESSAGE_KEYS = [
  "message",
  "error",
  "errorMessage",
  "reason",
  "description",
  "detail",
];

/** Reduces a key to letters and digits so "Error Message" matches "errorMessage". */
const canonical = (key) => String(key).toLowerCase().replace(/[^a-z0-9]/g, "");

/** Reads the first non-empty value among `keys`, ignoring case and separators. */
export const pickMessage = (source, keys = MESSAGE_KEYS) => {
  if (!source || typeof source !== "object") return null;

  const index = {};
  for (const key of Object.keys(source)) {
    const name = canonical(key);
    if (!(name in index)) index[name] = source[key];
  }

  for (const key of keys) {
    const value = index[canonical(key)];
    if (value !== undefined && value !== null && value !== "") return String(value);
  }

  return null;
};

/** Classifies a response body from its Content-Type, falling back to filename. */
export const detectResponseKind = (contentType = "", filename = "") => {
  const type = String(contentType).toLowerCase();
  const name = String(filename).toLowerCase();

  if (type.includes("json")) return RESPONSE_KIND.JSON;

  const looksLikeFile =
    FILE_CONTENT_HINTS.some((hint) => type.includes(hint)) ||
    FILE_NAME_HINTS.some((hint) => name.endsWith(hint));

  return looksLikeFile ? RESPONSE_KIND.FILE : RESPONSE_KIND.TEXT;
};

/** Extracts the filename from a Content-Disposition header. */
export const filenameFromDisposition = (header) => {
  if (!header) return "";

  // RFC 5987 form: filename*=UTF-8''bulk%20result.xlsx
  const encoded = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1].replace(/["']/g, "").trim());
    } catch (decodeError) {
      // Malformed encoding - fall through to the plain form below.
    }
  }

  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : "";
};

/** Builds a fallback name, e.g. "bulk-order-result-2026-10-07.xlsx". */
export const buildResultFilename = (prefix, contentType = "", filename = "") => {
  const source = `${contentType} ${filename}`.toLowerCase();
  const extension = source.includes("csv") ? "csv" : "xlsx";
  const stamp = new Date().toISOString().slice(0, 10);

  return `${prefix}-result-${stamp}.${extension}`;
};

/** Reads a Blob as text, with a FileReader fallback for older browsers. */
export const readBlobAsText = (blob) => {
  if (typeof blob.text === "function") return blob.text();

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
};

/** Parses text as JSON when it looks like JSON, otherwise returns it as-is. */
export const parseTextBody = (text) => {
  const trimmed = String(text || "").trim();
  if (trimmed === "") return null;

  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return JSON.parse(trimmed);
    } catch (parseError) {
      // Not valid JSON after all - treat it as plain text.
    }
  }

  return trimmed;
};

/** Saves a Blob to disk, mirroring Utils/CommonService.downloadDocument. */
export const downloadBlob = (blob, filename) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};
